import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@App/app/auth/auth.service';
import { ADMIN_ADDED_SUCCESS, SUCCESS } from '@App/app/configs/toastr-events.config';
import { SUCCESS_TOASTR_CONFIG } from '@App/app/configs/toastr-messages.config';
import { Endpoint } from '@App/app/entities/auth/endpoint.model';
import { environment } from '@App/environments/environment';
import { NbToastrService } from '@nebular/theme';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { selectAllUsers } from '../store/selectors/users.selectors';

@Component({
  selector: 'app-add-existing-user',
  templateUrl: './add-existing-user.component.html',
  styleUrls: ['./add-existing-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddExistingUserComponent implements OnInit {
  companyId: number | null;
  endpoint: Endpoint | null;

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });
  options: string[] = [];
  filteredEmailOptions$: Observable<string[]>;

  @ViewChild('autoInput') input: any;

  constructor(
    private authService: AuthService,
    private fb: FormBuilder,
    private httpClient: HttpClient,
    private router: Router,
    private toastrService: NbToastrService,
    private store: Store,
  ) {}

  ngOnInit(): void {
    this.authService.currentEndpoint$.pipe(untilDestroyed(this)).subscribe((endpoint) => {
      this.endpoint = endpoint;
      this.companyId = endpoint?.id || null;
    });

    combineLatest(
      this.store.select(selectAllUsers).pipe(untilDestroyed(this)),
      this.httpClient.get<string[]>(`${environment.loginUrl}/admin-emails`),
    ).subscribe(([companyUsers, adminEmails]) => {
      this.options = adminEmails.filter(
        (adminEmail) => !companyUsers.find((user) => user.email === adminEmail),
      );
      this.filteredEmailOptions$ = of(this.options);
      this.input?.nativeElement?.dispatchEvent(new Event('input')); // trigger change detection to update auto-complete dropdown
    });
  }

  onSubmit() {
    this.httpClient
      .post(`${environment.loginUrl}/add-admin-to-company`, {
        adminEmail: this.form.get('email')?.value,
        companyId: this.companyId,
      })
      .subscribe(() => {
        this.router.navigate([`/${this.endpoint?.id}/users`]);
        this.toastrService.show(ADMIN_ADDED_SUCCESS, SUCCESS, {
          ...SUCCESS_TOASTR_CONFIG,
        });
      });
  }

  required(...path: string[]) {
    return !!(
      this.form.get(`${path.join('.')}`)?.hasError('required') &&
      this.form.get(`${path.join('.')}`)?.touched
    );
  }

  getFilteredEmailOptions(value: string): Observable<string[]> {
    return of(value).pipe(map((filterString) => this.filter(filterString)));
  }

  onEmailChange() {
    this.filteredEmailOptions$ = this.getFilteredEmailOptions(this.input.nativeElement.value);
  }

  onEmailSelectionChange($event: any) {
    this.filteredEmailOptions$ = this.getFilteredEmailOptions($event);
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.options.filter((optionValue) => optionValue.toLowerCase().includes(filterValue));
  }
}
